import { LocalizeProvider } from "react-localize-redux";
import { renderToStaticMarkup } from "react-dom/server";
import baseTranslations from "./translations/global.json";
import chomDakarOverlay from "./translations/tenant/chom-dakar.json";
import { applyTenantOverlay, pickTenantOverlay, resolveTenantFromApiUrl } from "./translations/tenantOverlay";

// SHER-211 — apply tenant-specific translation overrides at app init.
// The tenant slug is derived from VITE_APP_API_URL (every tenant already has
// its own API host configured in the build's .env), so there's nothing extra
// to remember in the deploy. Hosts that don't match a known tenant fall back
// to the base translations unchanged.
const TENANT_OVERLAYS = {
    "chom-dakar": chomDakarOverlay,
};
const _tenantSlug = resolveTenantFromApiUrl(import.meta.env.VITE_APP_API_URL);
const _tenantOverlay = pickTenantOverlay(TENANT_OVERLAYS, _tenantSlug);
const globalTranslations = _tenantOverlay
    ? applyTenantOverlay(baseTranslations, _tenantOverlay)
    : baseTranslations;
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import createTheme from "./theme";
import { CustomThemeProvider } from "./themeProvider";
import { Helmet } from "react-helmet";
import { ThemeProvider } from "styled-components";
import { create } from "jss";
import StylesProvider from '@mui/styles/StylesProvider';
import jssPreset from '@mui/styles/jssPreset';
import {
    QueryClient,
    QueryClientProvider,
  } from 'react-query'
import store from "./redux/store/index";
import axios from 'axios';
import DatePickerProvider from "./components/DatePickerProvider";
import { initDiagnostics, addBreadcrumb, flush, jwtShape } from "./utils/diagnostics/breadcrumbs";
import { captureAppState } from "./utils/diagnostics/appState";
import { installTelemetryInterceptors, startTelemetry } from "./utils/diagnostics/telemetry";
import { runCompatibilityCheck } from "./utils/diagnostics/compatibility";

// Subscribe to the Redux store and init diagnostics the moment a currentInvestigation uuid appears.
// This is outside React's render cycle so it works even if any layout-level useEffect misfires.
let __diagInitedFor = null;
store.subscribe(() => {
    try {
        const uuid = store.getState()?.investigations?.currentInvestigation?.uuid;
        if (uuid && __diagInitedFor !== uuid) {
            __diagInitedFor = uuid;
            // __GIT_HASH__ is injected by Vite at build time (see vite.config.js).
            // Lets us attribute breadcrumbs to a specific client bundle when
            // triaging diagnostic dumps — previously the `clientBuildCommit`
            // field was always empty, so we couldn't tell if a flush came from
            // a user stuck on a stale cached build.
            const buildCommit = typeof __GIT_HASH__ !== 'undefined' ? __GIT_HASH__ : undefined;
            initDiagnostics({ investigationUuid: uuid, apiBaseUrl: import.meta.env.VITE_APP_API_URL, clientBuildCommit: buildCommit });
        }
    } catch (_) { /* never break app on diagnostics */ }
});

// Catch the errors React's ErrorBoundary misses:
//   - Event-handler throws (onSubmit, onClick, ...)
//   - Unhandled Promise rejections (async functions that throw without a .catch)
//   - setTimeout / setInterval exceptions
// Without these two listeners, a form-submit crash (e.g. missing birthdate → TypeError on value.getMonth)
// would never reach the breadcrumb pipeline.
if (typeof window !== "undefined") {
    window.addEventListener("error", (e) => {
        try {
            addBreadcrumb("exception", "window.error", {
                errorName: e.error?.name,
                errorMessage: (e.error?.message || e.message || "").slice(0, 200),
                route: window.location?.pathname,
                ...captureAppState(), // SHER-499: redacted app state at the crash moment
            });
            flush("window-error");
        } catch (_) { /* never break app on diagnostics */ }
    });
    window.addEventListener("unhandledrejection", (e) => {
        try {
            const r = e.reason;
            addBreadcrumb("exception", "unhandled-rejection", {
                errorName: r?.name,
                errorMessage: (r?.message || String(r)).slice(0, 200),
                route: window.location?.pathname,
                ...captureAppState(), // SHER-499: redacted app state at the crash moment
            });
            flush("unhandled-rejection");
        } catch (_) { /* never break app on diagnostics */ }
    });

    // Runtime instrumentation — trace writes/removes of the two auth keys we care about.
    // ATTADIR's session loses `rawKeyResearcher` mid-session despite keeping her JWT,
    // and no grep of the codebase finds a path that does that. So we monkey-patch
    // Storage.prototype and record a breadcrumb whenever `password` or `rawKeyResearcher`
    // are touched, plus every `.clear()`. The short stack (5 frames) tells us the callsite.
    // Uses a module-level flag so HMR/double-import doesn't re-wrap (which would skew frames).
    if (!window.__sherwoodStorageInstrumented) {
        window.__sherwoodStorageInstrumented = true;
        // CHOM 2026-06: /researcher/validate is rejected with the JWT *present but
        // malformed*, so the corrupting/clearing of `jwt` itself must be traced too,
        // not just the encryption keys.
        const AUTH_KEYS = new Set(["password", "rawKeyResearcher", "jwt"]);
        const shortStack = () => {
            try {
                return new Error().stack?.split("\n").slice(3, 8).join(" | ").slice(0, 300) || "";
            } catch (_) { return ""; }
        };
        const wrap = (fn, op) => new Proxy(fn, {
            apply(target, thisArg, args) {
                try {
                    if (op === "clear") {
                        addBreadcrumb("state", "storage.clear", {
                            errorMessage: shortStack(),
                            route: window.location?.pathname,
                        });
                        flush("storage-clear");
                    } else {
                        const key = args[0];
                        if (AUTH_KEYS.has(key)) {
                            // On a `jwt` write, capture the *shape* of the value being
                            // stored (length + segment count, never the token) so a
                            // corrupting write — e.g. the literal "undefined" or a
                            // truncated token — is visible at the callsite that did it.
                            const shapeFields = (key === "jwt" && op === "setItem")
                                ? jwtShape(args[1])
                                : {};
                            addBreadcrumb("state", `storage.${op}`, {
                                errorName: String(key),
                                errorMessage: shortStack(),
                                route: window.location?.pathname,
                                ...shapeFields,
                            });
                            // Flush on any removal of an auth key, and on a `jwt` write
                            // that is structurally bad (so the bad writer is captured live).
                            if (op === "removeItem" || (shapeFields.jwtShape && shapeFields.jwtShape !== "jwt-3seg")) {
                                flush(`storage-${op}`);
                            }
                        }
                    }
                } catch (_) { /* never break app on diagnostics */ }
                return Reflect.apply(target, thisArg, args);
            },
        });
        try {
            Storage.prototype.setItem = wrap(Storage.prototype.setItem, "setItem");
            Storage.prototype.removeItem = wrap(Storage.prototype.removeItem, "removeItem");
            Storage.prototype.clear = wrap(Storage.prototype.clear, "clear");
        } catch (_) { /* if patching fails, fall back silently */ }

        // Cross-tab detection. The Storage.prototype patch above only sees writes
        // made by THIS tab; the `storage` event fires only in OTHER tabs when
        // localStorage changes. CHOM devices are shared and staff keep several
        // tabs open, so a logout/login in one tab can wipe `jwt`/`rawKeyResearcher`
        // out from under an active tab — this catches exactly that.
        try {
            window.addEventListener("storage", (e) => {
                try {
                    // e.key === null means localStorage.clear() was called elsewhere.
                    if (e.key !== null && !AUTH_KEYS.has(e.key)) return;
                    const op = e.key === null ? "clear" : (e.newValue === null ? "removeItem" : "setItem");
                    const shapeFields = (e.key === "jwt" && e.newValue !== null)
                        ? jwtShape(e.newValue)
                        : {};
                    addBreadcrumb("state", "storage.cross-tab", {
                        errorName: e.key === null ? "(clear)" : String(e.key),
                        reason: op,
                        crossTab: true,
                        route: window.location?.pathname,
                        ...shapeFields,
                    });
                    // Another tab nuking our auth state is the smoking gun — flush now.
                    flush("storage-cross-tab");
                } catch (_) { /* never break app on diagnostics */ }
            });
        } catch (_) { /* ignore */ }
    }
}

const jss = create({
    ...jssPreset(),
    insertionPoint: document.getElementById("jss-insertion-point"),
});

// SHER-323/SHER-320 — instrument the GLOBAL axios default too. Many services
// import the bare `axios` default instead of the shared utils/axios instance,
// so the traceability headers + telemetry must also be attached here. All of
// it is wrapped in try/catch — instrumentation must never break a request.
try { startTelemetry(); } catch (_) { /* never break app */ }

// Install the shared traceability + telemetry interceptors on the global axios
// default. No breadcrumbs here (the shared utils/axios instance owns the
// breadcrumb pipeline); this only stamps the headers and records telemetry.
try { installTelemetryInterceptors(axios); } catch (_) { /* never break app */ }

// SHER-323 — boot-time compatibility check (GET /version). Non-blocking; on a
// too-old client it renders a "please refresh" banner and triggers the PWA SW
// update. Fully guarded so a failed/absent endpoint never blocks the app.
try { runCompatibilityCheck(); } catch (_) { /* never break app */ }

// Create a client
const queryClient = new QueryClient()


function OtherProviders(props){
    
    return (
        <QueryClientProvider client={queryClient}>
            <BrowserRouter>
                <StylesProvider jss={jss}>
                    <CustomThemeProvider>
                        <LocalizeProvider initialize={{
                            languages: [
                            { name: "English", code: "en" },
                            { name: "Spanish", code: "es" },
                            { name: "Français", code: "fr" }
                            ],
                            translation: globalTranslations,
                            options: {
                            defaultLanguage: localStorage.getItem("language") ? localStorage.getItem("language") :  "en",
                                renderToStaticMarkup: renderToStaticMarkup
                            }
                        }}>
                            <DatePickerProvider>
                                { props.children }
                            </DatePickerProvider>
                        </LocalizeProvider>
                    </CustomThemeProvider>
                </StylesProvider>
            </BrowserRouter>
        </QueryClientProvider>
    );
}


export default function ProviderSherwood(props){
    const title = import.meta.env.VITE_APP_PRODUCT === "HOSPITAL" ? "Hospital platform" : "Researcher platform"
    if (props.initStore) {
        props.initStore.map((initObject) => {
            store.dispatch({ type: initObject.type, payload: initObject.payload })
        })
    }
    return (
        <Provider store={store}>
            <Helmet
                titleTemplate="%s | Sherwood"
                defaultTitle={title}
            />
            <OtherProviders>
                { props.children }
            </OtherProviders>
        </Provider>
    )
    
}
